import React, { useState } from "react"

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content

const inputCls =
  "block w-full rounded-lg border border-gray-300 dark:border-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:bg-gray-50 disabled:text-gray-500"
const labelCls = "block text-sm font-medium text-gray-700 dark:text-gray-300"
const primaryBtn =
  "inline-flex items-center justify-center rounded-lg bg-brand-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-brand-700 active:scale-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"

const TIMEZONES = [
  "UTC", "America/New_York", "America/Chicago", "America/Denver", "America/Los_Angeles",
  "America/Toronto", "America/Vancouver", "Europe/London", "Europe/Berlin", "Europe/Paris",
  "Europe/Amsterdam", "Europe/Stockholm", "Europe/Warsaw", "Asia/Tokyo", "Asia/Singapore",
  "Asia/Seoul", "Asia/Shanghai", "Asia/Kolkata", "Australia/Sydney", "Pacific/Auckland",
]

const CURRENCIES = [
  { code: "USD", label: "USD — US Dollar" },
  { code: "EUR", label: "EUR — Euro" },
  { code: "GBP", label: "GBP — British Pound" },
  { code: "CAD", label: "CAD — Canadian Dollar" },
  { code: "AUD", label: "AUD — Australian Dollar" },
  { code: "JPY", label: "JPY — Japanese Yen" },
  { code: "SEK", label: "SEK — Swedish Krona" },
  { code: "NOK", label: "NOK — Norwegian Krone" },
  { code: "DKK", label: "DKK — Danish Krone" },
  { code: "CHF", label: "CHF — Swiss Franc" },
]

export function ProfileForm({ profile, settingsPath, copy }) {
  const [name, setName] = useState(profile.name)
  const [timezone, setTimezone] = useState(profile.timezone)
  const [currency, setCurrency] = useState(profile.currency)
  const [busy, setBusy] = useState(false)
  const [toast, setToast] = useState(null)
  const [errors, setErrors] = useState([])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (busy) return
    setBusy(true)
    setErrors([])
    setToast(null)
    try {
      const res = await fetch(settingsPath, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
          Accept: "application/json",
        },
        body: JSON.stringify({ partner: { name, timezone, currency } }),
      })
      if (res.ok) {
        setToast(copy.saveSuccess)
      } else {
        const data = await res.json()
        setErrors(data.errors || [copy.errorGeneric])
      }
    } catch {
      setErrors([copy.errorGeneric])
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div>
        <label className={labelCls}>{copy.nameLabel}</label>
        <input
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          disabled={busy}
          className={`mt-1 ${inputCls}`}
        />
      </div>

      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label className={labelCls}>{copy.timezoneLabel}</label>
          <select
            value={timezone}
            onChange={(e) => setTimezone(e.target.value)}
            disabled={busy}
            className={`mt-1 ${inputCls}`}
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz}>{tz}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={labelCls}>{copy.currencyLabel}</label>
          <select
            value={currency}
            onChange={(e) => setCurrency(e.target.value)}
            disabled={busy}
            className={`mt-1 ${inputCls}`}
          >
            {CURRENCIES.map(({ code, label }) => (
              <option key={code} value={code}>{label}</option>
            ))}
          </select>
        </div>
      </div>

      <div>
        <label className={labelCls}>{copy.statusLabel}</label>
        <p className="mt-1 text-sm capitalize text-gray-600 dark:text-gray-400">{profile.status}</p>
      </div>

      {errors.length > 0 && (
        <ul className="rounded-lg border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/40 p-3 text-sm text-red-700 dark:text-red-300 space-y-1">
          {errors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}

      {toast && (
        <p className="text-sm font-medium text-green-700 dark:text-green-300">{toast}</p>
      )}

      <div className="flex justify-end">
        <button type="submit" disabled={busy} className={primaryBtn}>
          {busy ? copy.saving : copy.save}
        </button>
      </div>
    </form>
  )
}
